import React, { useEffect, useRef } from 'react';
import {
  useGetFilterRecommend,
  useRecentReadApproval,
  useSearchApproval,
} from '@/hooks/apiHooks';
import { useFormContext } from 'react-hook-form';
import NewCustomInput from '@/components/CustomInput';
import { setJwtToken } from '@/utils/tokens';
import { getIntranetDocUrl } from '@/utils/urls';
import IframeApprovalModal from '@/components/Approval/IframeApprovalModal';
import { ModalRef } from '@/components/@IntranetLibrary/Modal';
import useModal from '@/hooks/useModal';
import { ContentsLayout } from '@/components/Approval/RightContainer/SearchApproval/styles';
import RequestSearch from '@/components/Approval/RightContainer/SearchApproval/RequestSearch';
import { useDebounce } from 'usehooks-ts';
import { localFilterToParam } from '@/components/Approval/RightContainer';
import { INIT_WEB_APPROVAL_FILTER_STATE } from '@/recoil/init_data';
import InitSearchView from '@/components/Approval/RightContainer/SearchApproval/InitSearchView';
import { SearchData } from '@/types/api';
import SearchContainer from '@/components/Approval/RightContainer/SearchContainer';
import useSearch from '@/components/Approval/RightContainer/SearchApproval/hooks/useSearch';
import { useAtom } from 'jotai';
import { approvalFilter } from '@/recoil/atom';
import { EventPageAreaName } from '@/constant/gtm';
import { Stack } from '@mui/material';
import RecentViewedItem from '@/components/Work/WorkSNB/SNBRecent/RecentViewedItem';

type SearchApprovalProps = {
  isTablet: boolean;
  onSubmit: ({ keyword, searchFields }: SearchData) => void;
};

export const options = [
  { label: '전체', value: 'subject,content' },
  { label: '제목', value: 'subject' },
  { label: '내용', value: 'content' },
];

const SearchApproval = ({ isTablet, onSubmit }: SearchApprovalProps) => {
  const [filters] = useAtom(approvalFilter);
  const methods = useFormContext<SearchData>();
  const { watch, setValue, getValues } = methods;
  const searchString = watch('keyword');
  const debouncedValue = useDebounce(searchString, 500);
  const searchMode = debouncedValue.length > 1;

  const { data: keywordData, mutate: keywordMutate } = useGetFilterRecommend();
  const { data: recentSearchData, mutate: recentSearchMutate } =
    useRecentReadApproval();

  const { data: approvalData, isLoading } = useSearchApproval({
    ...localFilterToParam(INIT_WEB_APPROVAL_FILTER_STATE),
    listSize: 5,
    keyword: searchMode ? debouncedValue : '',
    searchFields: watch('searchFields'),
  });

  useEffect(() => {
    setValue('keyword', filters.keyword ?? '');
    inputRef.current?.blur();
  }, [filters.id, setValue]);

  const keywordDataLength = (keywordData && keywordData.keywords?.length) || 0;
  const approvalModalRef = useRef<ModalRef>(null);

  const { openModal: openApprovalModal, closeModal: closeApprovalModal } =
    useModal(approvalModalRef);

  const handleCloseModal = () => {
    closeApprovalModal();
    recentSearchMutate();
  };

  const {
    docId,
    isFocus,
    inputRef,
    menuRef,
    t,
    handleFocus,
    handleKeyUp,
    focusedIndex,
    handleSubmit,
    setItems,
    handleOpen,
  } = useSearch({
    pageArea: EventPageAreaName.WORK_APPROVAL,
    areaName: 'Approval 목록',
    onSubmit,
    keywordDataLength,
    searchDocIdData: approvalData?.data.map((res) => res.docId),
    recentDocIDData: recentSearchData?.map((res) => res.docId),
    keywordData: keywordData?.keywords,
    openApprovalModal: openApprovalModal,
  });

  useEffect(() => {
    keywordMutate();
  }, [approvalData]);

  useEffect(() => {
    isFocus && recentSearchMutate();
  }, [isFocus]);

  const haveSearchData = approvalData && approvalData.data.length > 0;

  useEffect(() => {
    setItems(
      searchMode
        ? approvalData?.data.length ?? 0
        : (keywordData?.keywords?.length ?? 0) +
            (recentSearchData?.length ?? 0),
    );
  }, [keywordData, recentSearchData, approvalData, searchMode]);

  const handleApprovalOpen = (id: string) => {
    handleOpen(id);
    handleSubmit({ keyword: debouncedValue });
  };

  return (
    <SearchContainer
      ref={menuRef}
      isTablet={isTablet}
      searchComponent={
        <>
          <NewCustomInput
            ref={inputRef}
            isFocus={isFocus}
            onSubmit={handleSubmit}
            placeHolder={t('검색어를 입력해 주세요.')}
            handleFocus={handleFocus}
            handleKeyUp={handleKeyUp}
            style={{
              width: '240px',
              height: '36px',
              alignItems: 'center',
            }}
          />
          <ContentsLayout isFocus={isFocus}>
            {searchMode ? (
              haveSearchData ? (
                <Stack gap={'4px'} sx={{ padding: '16px 12px' }}>
                  {approvalData.data.map((item, index) => (
                    <RecentViewedItem
                      key={item.docId}
                      id={item.docId}
                      title={item.subject}
                      authorName={item.author.name}
                      authorPosition={item.author.positionName}
                      viewedDate={item.authoredAt || item.createdAt}
                      onClick={() => handleApprovalOpen(item.docId)}
                      isFocused={focusedIndex === index}
                    />
                  ))}
                </Stack>
              ) : (
                <RequestSearch
                  title={t(
                    '조건에 해당하는 문서가 없어요.\n검색어를 확인해 주세요.',
                  )}
                  isLoading={isLoading}
                />
              )
            ) : (
              <InitSearchView
                keywordData={keywordData?.keywords}
                recentSearchData={recentSearchData}
                handleSearch={handleSubmit}
                handleOpen={handleOpen}
                mutate={keywordMutate}
                focusedIndex={focusedIndex}
              />
            )}
          </ContentsLayout>
        </>
      }
      modalComponent={
        <IframeApprovalModal
          ref={approvalModalRef}
          url={setJwtToken(getIntranetDocUrl(docId, true))}
          handleClose={handleCloseModal}
        />
      }
    />
  );
};

export default SearchApproval;
